import { v4 } from "uuid";

const getIdMap = <T extends object = Record<string, unknown>>(object: T) => {
  const idMap: Record<string, string> = {};

  // Want to create new ids for any revision tables OR the deviation survey
  // TODO - need better solution to determine if deviation survey since it
  // doesn't have winning_revision_id since it's not revised
  const isRevisionTable = "winning_revision_id" in object;
  const isDeviationSurvey = "directional_data_set" in object;

  if (isRevisionTable || isDeviationSurvey) {
    // Traverse object recursively
    Object.entries(object).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      if (typeof value === "string") {
        if (key === "id") {
          if (!idMap[value as string]) {
            idMap[value as string] = "";
          }
        }
      } else if (Array.isArray(value)) {
        if (value.some(item => typeof item === "string")) {
          value.forEach(item => {
            if (!idMap[item]) {
              idMap[item] = "";
            }
          });
        } else {
          value.map(row => Object.assign(idMap, getIdMap(row)));
        }
      } else if (Object.keys(value as Record<string, unknown>).includes("id")) {
        Object.assign(idMap, getIdMap(value as Record<string, unknown>));
      }
    });
  }

  return Object.keys(idMap).reduce<Record<string, string>>(
    (map, id) => Object.assign(map, { [id]: v4() }),
    {}
  );
};

const replaceIds = <T extends object = Record<string, unknown>>(
  object: T,
  newKeys: Record<string, string>
): T => {
  const out = JSON.parse(JSON.stringify(object));
  // TODO - need better solution to determine if deviation survey since it
  // doesn't have winning_revision_id since it's not revised
  const isRevisionTable = "winning_revision_id" in object;
  const isDeviationSurvey = "directional_data_set" in object;
  // Traverse object recursively
  Object.entries(out).forEach(([key, value]) => {
    if (!value) {
      return value;
    }
    if (isRevisionTable || isDeviationSurvey) {
      if (typeof value === "string") {
        if (key === "winning_revision_id") {
          out[key] = null;
        } else if (key === "id") {
          // Don't want to add cloned_from_journal_id to deviation surveys
          // or if an xref is added yet not persisted to database/cache and
          // user tries to clone the scenario.
          if (isRevisionTable && out.winning_revision_id !== null) {
            out.cloned_from_journal_id = out[key];
          }
          out[key] = newKeys[value];
        } else if (newKeys[value]) {
          if (key !== "journal_author_id" && key !== "author_id") {
            out[key] = newKeys[value];
          }
        }
      } else if (Array.isArray(value)) {
        // Todo: Ideally this checks for item in array that matches UUID via regex
        if (value.some(item => typeof item === "string")) {
          out[key] = value.map(existingId => newKeys[existingId]);
        } else {
          out[key] = value.map(item => replaceIds(item, newKeys));
        }
      } else if (Object.keys(value as Record<string, unknown>).includes("id")) {
        out[key] = replaceIds(value as Record<string, unknown>, newKeys);
      }
    }

    return out;
  });

  return out;
};

export default function <T = Record<string, unknown>>(
  journalObject: T | undefined
): T | null {
  if (journalObject) {
    const idMap = getIdMap(journalObject);
    const transplantedJournal = replaceIds(journalObject, idMap);
    return transplantedJournal;
  }
  return null;
}
